import { CARDAMOM_GRADES, IMAGES, PHOTO_LABEL } from "@/lib/constants";
import { useEnquiry } from "@/context/EnquiryContext";
import { Reveal } from "@/components/Reveal";

export const CardamomGrading = ({ showGallery = true }) => {
  const { openEnquiry } = useEnquiry();

  return (
    <section id="cardamom-grades" className="py-16 bg-spice-sand border-y border-spice-line px-4 sm:px-6" data-testid="cardamom-grading-section">
      <div className="max-w-7xl mx-auto space-y-8">
        <Reveal className="max-w-3xl space-y-2">
          <span className="text-xs font-bold uppercase tracking-widest text-spice-green">Cardamom grades</span>
          <h2 className="text-2xl sm:text-3xl font-bold text-spice-ink">Indicative Cardamom Size Categories</h2>
          <p className="text-sm text-spice-muted">
            Cardamom is offered grade-wise by indicative pod size. Availability of each category depends on the lot
            currently in stock.
          </p>
        </Reveal>

        <Reveal>
          <div className="overflow-hidden rounded-lg border border-spice-line bg-white">
            <table className="w-full text-left border-collapse" data-testid="cardamom-grade-table">
              <caption className="sr-only">Indicative cardamom grades, commercial category, typical buyers and availability</caption>
              <thead className="bg-spice-cream">
                <tr className="text-[11px] uppercase tracking-wider text-spice-green">
                  <th scope="col" className="px-4 py-3 font-bold">Indicative size</th>
                  <th scope="col" className="px-4 py-3 font-bold">Commercial category</th>
                  <th scope="col" className="px-4 py-3 font-bold hidden sm:table-cell">Typical buyer / application</th>
                  <th scope="col" className="px-4 py-3 font-bold hidden lg:table-cell">Availability</th>
                  <th scope="col" className="px-4 py-3 font-bold text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {CARDAMOM_GRADES.map((grade, i) => (
                  <tr key={grade.size} className="border-t border-spice-line align-top" data-testid={`grade-row-${i + 1}`}>
                    <td className="px-4 py-4 text-sm font-bold text-spice-ink whitespace-nowrap">{grade.size}</td>
                    <td className="px-4 py-4 text-xs text-spice-ink font-medium">
                      {grade.category}
                      <span className="block sm:hidden text-[11px] text-spice-muted font-normal pt-1">{grade.buyers}</span>
                      <span className="block lg:hidden text-[11px] text-spice-subtle font-normal pt-1">Subject to current lot</span>
                    </td>
                    <td className="px-4 py-4 text-xs text-spice-muted hidden sm:table-cell">{grade.buyers}</td>
                    <td className="px-4 py-4 text-xs text-spice-subtle hidden lg:table-cell">Subject to current lot</td>
                    <td className="px-4 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => openEnquiry({ product: "Green Cardamom", grade: grade.size, source: "grade-table" })}
                        data-testid={`grade-availability-button-${i + 1}`}
                        className="inline-flex whitespace-nowrap items-center justify-center px-3 py-2 bg-spice-green text-white text-[11px] font-bold rounded-[9px] hover:bg-spice-green-dark transition-colors"
                      >
                        Check Current Availability
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>

        {showGallery && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {IMAGES.gallery.map((img, i) => (
              <Reveal key={img.label} delay={i * 70}>
                <figure className="aspect-[4/3] rounded overflow-hidden border border-spice-line relative group" data-testid={`grading-gallery-${i + 1}`}>
                  <img
                    src={img.url}
                    alt={img.alt}
                    width={img.width}
                    height={img.height}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <figcaption className="absolute bottom-0 left-0 right-0 text-[10px] text-white bg-spice-green-dark/85 px-2 py-1">
                    {img.label} • {PHOTO_LABEL}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        )}

        <Reveal>
          <div className="p-4 bg-white rounded border-l-4 border-spice-green text-xs text-spice-muted" data-testid="grading-disclaimer">
            <strong className="text-spice-ink">Size and grade descriptions are indicative.</strong> Final supply is based
            on the actual available lot, approved sample and written commercial quotation. Mixed or segregated material
            is available on request.
          </div>
        </Reveal>
      </div>
    </section>
  );
};
